import { jsPDF } from "jspdf";

export interface FinancialOperationPdfRow {
  bookingDate: string;
  bank: string;
  account: string;
  counterparty: string;
  title: string;
  amount: number;
  currency: string;
  direction: "Credit" | "Debit";
}

export interface FinancialOperationsPdfInput {
  rows: FinancialOperationPdfRow[];
  from: string;
  to: string;
  scopeLabel: string;
}

let fontRegularB64: string | null = null;
let fontBoldB64: string | null = null;

async function loadFontB64(url: string): Promise<string> {
  const res = await fetch(url);
  const buf = await res.arrayBuffer();
  const bytes = new Uint8Array(buf);
  let binary = "";
  const chunk = 8192;
  for (let i = 0; i < bytes.length; i += chunk)
    binary += String.fromCharCode(...bytes.subarray(i, i + chunk));
  return btoa(binary);
}

const fmtDate = (iso: string) =>
  iso ? new Date(iso).toLocaleDateString("pl-PL") : "—";

const fmtAmt = (amount: number, currency: string) =>
  `${amount.toLocaleString("pl-PL", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${currency}`;

export async function generateFinancialOperationsPdf(input: FinancialOperationsPdfInput): Promise<void> {
  if (!fontRegularB64) fontRegularB64 = await loadFontB64("/fonts/LiberationSans-Regular.ttf");
  if (!fontBoldB64)    fontBoldB64    = await loadFontB64("/fonts/LiberationSans-Bold.ttf");

  const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
  doc.addFileToVFS("LiberationSans-Regular.ttf", fontRegularB64);
  doc.addFont("LiberationSans-Regular.ttf", "LiberationSans", "normal");
  doc.addFileToVFS("LiberationSans-Bold.ttf", fontBoldB64);
  doc.addFont("LiberationSans-Bold.ttf", "LiberationSans", "bold");

  const ML = 10, MT = 10, PW = 277, PH = 190;
  const ROW_H = 7, HDR_H = 9;

  const PAD = 1.5;    // poziomy padding komórki (mm)
  const PAD_V = 1.6;  // pionowy padding komórki (mm)
  // Wysokość pojedynczej linii dla danego rozmiaru czcionki (pt → mm, z interlinią).
  const lineHeightMm = (size: number) => size * 0.3528 * 1.15;

  const cell = (
    x: number, y: number, w: number, _h: number,
    text: string, align: "left" | "right" | "center",
    bold = false, size = 8,
  ) => {
    doc.setFont("LiberationSans", bold ? "bold" : "normal");
    doc.setFontSize(size);
    const tx = align === "right" ? x + w - PAD : align === "center" ? x + w / 2 : x + PAD;
    const lines = doc.splitTextToSize(text, w - 2 * PAD);
    // Wyrównanie tekstu do góry komórki — przy dynamicznej wysokości wiersza
    // wielolinijkowy tekst mieści się w jego granicach.
    const ty = y + PAD_V + lineHeightMm(size) * 0.78;
    doc.text(lines, tx, ty, { align, lineHeightFactor: 1.15 });
  };

  // ── Nagłówek strony ─────────────────────────────────────────────────────
  let y = MT;
  cell(ML, y, PW, 7, "Operacje finansowe", "left", true, 13);
  y += 7;
  cell(ML, y, PW, 5,
    `${input.scopeLabel}  ·  ${fmtDate(input.from)} – ${fmtDate(input.to)}  ·  Liczba pozycji: ${input.rows.length}`,
    "left", false, 8);
  y += 6;

  // ── Kolumny ─────────────────────────────────────────────────────────────
  // Stałe szerokości: Data(22) + Bank(28) + Rachunek(32) + Kontrahent(52) + Wpływy(36) + Wydatki(36) = 206
  // Tytuł dostaje resztę: 277 − 206 = 71
  const COL_W = { data: 22, bank: 28, rachunek: 32, kontrahent: 52, tytul: 71, wplywy: 36, wydatki: 36 };

  type ColDef = { header: string; w: number; align: "left" | "right" };
  const COLS: ColDef[] = [
    { header: "Data",       w: COL_W.data,       align: "left"  },
    { header: "Bank",       w: COL_W.bank,       align: "left"  },
    { header: "Rachunek",   w: COL_W.rachunek,   align: "left"  },
    { header: "Kontrahent", w: COL_W.kontrahent, align: "left"  },
    { header: "Tytuł",      w: COL_W.tytul,      align: "left"  },
    { header: "Wpływy",     w: COL_W.wplywy,     align: "right" },
    { header: "Wydatki",    w: COL_W.wydatki,    align: "right" },
  ];

  // Linia oddzielająca nagłówek Wpływy / Wydatki od reszty
  const xWplywy  = ML + COL_W.data + COL_W.bank + COL_W.rachunek + COL_W.kontrahent + COL_W.tytul;
  const xWydatki = xWplywy + COL_W.wplywy;

  const drawTableHeader = (yy: number) => {
    doc.setFillColor(235, 232, 250);
    doc.rect(ML, yy, PW, HDR_H, "F");

    // Subtelne tło dla kolumn kwotowych
    doc.setFillColor(225, 220, 245);
    doc.rect(xWplywy, yy, COL_W.wplywy + COL_W.wydatki, HDR_H, "F");

    let x = ML;
    COLS.forEach((col) => {
      cell(x, yy, col.w, HDR_H, col.header, col.align, true, 8);
      x += col.w;
    });

    // Pionowa linia przed Wpływy
    doc.setDrawColor(180, 170, 210);
    doc.line(xWplywy, yy, xWplywy, yy + HDR_H);
    doc.line(xWydatki, yy, xWydatki, yy + HDR_H);

    doc.setDrawColor(180, 170, 210);
    doc.line(ML, yy + HDR_H, ML + PW, yy + HDR_H);
    return yy + HDR_H;
  };

  y = drawTableHeader(y);

  // Wysokość wiersza dopasowana do najdłuższej (najbardziej zawiniętej) komórki,
  // aby tekst zawsze mieścił się w granicach komórki. Minimum = ROW_H.
  const measureRowHeight = (vals: string[]): number => {
    doc.setFont("LiberationSans", "normal");
    doc.setFontSize(7.5);
    let maxLines = 1;
    COLS.forEach((col, ci) => {
      const lines = doc.splitTextToSize(vals[ci] || "", col.w - 2 * PAD);
      if (lines.length > maxLines) maxLines = lines.length;
    });
    return Math.max(ROW_H, maxLines * lineHeightMm(7.5) + 2 * PAD_V);
  };

  // ── Wiersze ─────────────────────────────────────────────────────────────
  // Sumy per waluta (może być wielowalutowe)
  const creditByCurrency: Record<string, number> = {};
  const debitByCurrency:  Record<string, number> = {};

  input.rows.forEach((row, i) => {
    const isCredit = row.direction === "Credit";
    const cur = row.currency || "PLN";
    if (isCredit) creditByCurrency[cur] = (creditByCurrency[cur] ?? 0) + row.amount;
    else          debitByCurrency[cur]  = (debitByCurrency[cur]  ?? 0) + row.amount;

    const wplywyVal  = isCredit ? fmtAmt(row.amount, cur) : "";
    const wydatkiVal = isCredit ? "" : fmtAmt(row.amount, cur);

    const vals = [
      row.bookingDate ? fmtDate(row.bookingDate) : "—",
      row.bank || "—",
      row.account || "—",
      row.counterparty || "—",
      row.title || "—",
      wplywyVal,
      wydatkiVal,
    ];

    // Wysokość wiersza dopasowana do treści (wielolinijkowy Kontrahent / Tytuł).
    const rowH = measureRowHeight(vals);

    if (y + rowH > MT + PH) {
      doc.addPage();
      y = MT;
      y = drawTableHeader(y);
    }

    if (i % 2 === 0) {
      doc.setFillColor(250, 248, 255);
      doc.rect(ML, y, PW, rowH, "F");
    }

    let x = ML;
    COLS.forEach((col, ci) => {
      const isWplywy = ci === 5;
      if (isWplywy) doc.setTextColor(21, 127, 59);
      else          doc.setTextColor(28, 27, 31);

      cell(x, y, col.w, rowH, vals[ci], col.align, false, 7.5);
      x += col.w;
    });
    doc.setTextColor(0, 0, 0);

    // Pionowe linie dla kolumn kwotowych + linia dolna wiersza
    doc.setDrawColor(220, 215, 230);
    doc.line(xWplywy,  y, xWplywy,  y + rowH);
    doc.line(xWydatki, y, xWydatki, y + rowH);
    doc.line(ML, y + rowH, ML + PW, y + rowH);
    y += rowH;
  });

  // ── Wiersz sum ───────────────────────────────────────────────────────────
  const currencies = [...new Set([...Object.keys(creditByCurrency), ...Object.keys(debitByCurrency)])];
  const sumRowCount = Math.max(currencies.length, 1);

  if (y + ROW_H * sumRowCount > MT + PH) { doc.addPage(); y = MT; }

  doc.setFillColor(220, 215, 240);
  doc.rect(ML, y, PW, ROW_H * sumRowCount, "F");

  // Nagłówek „Razem" po lewej
  cell(ML, y, xWplywy - ML, ROW_H * sumRowCount, "Razem:", "right", true, 8);

  // Sumy per waluta w kolumnach
  currencies.forEach((cur, idx) => {
    const rowY = y + idx * ROW_H;
    const creditSum = creditByCurrency[cur] ?? 0;
    const debitSum  = debitByCurrency[cur]  ?? 0;

    if (creditSum > 0) {
      doc.setTextColor(21, 127, 59);
      cell(xWplywy, rowY, COL_W.wplywy, ROW_H, fmtAmt(creditSum, cur), "right", true, 7.5);
    }
    if (debitSum > 0) {
      doc.setTextColor(28, 27, 31);
      cell(xWydatki, rowY, COL_W.wydatki, ROW_H, fmtAmt(debitSum, cur), "right", true, 7.5);
    }
  });
  doc.setTextColor(0, 0, 0);

  // Pionowe linie w wierszu sum
  doc.setDrawColor(180, 170, 210);
  doc.line(xWplywy,  y, xWplywy,  y + ROW_H * sumRowCount);
  doc.line(xWydatki, y, xWydatki, y + ROW_H * sumRowCount);

  doc.save(`operacje-finansowe-${input.from}-${input.to}.pdf`);
}
